import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Check, Minus, Sparkles, X } from 'lucide-react';
import { countAiScope } from '../../../utils/complianceFilters';

/**
 * AiComplianceReviewModal
 *
 * Centered modal for reviewer "Review with AI": draft checklist + cell
 * preview + run/cancel. Visual pattern matches `BulkComplianceModal`
 * (dark #111 shell, pill buttons, custom checkboxes).
 *
 * Draft selection is controlled by the parent (`ReviewerCampaignPage`).
 * On run, the modal closes immediately; SSE updates stream via
 * `useAiComplianceReview`.
 *
 * Preview counts use `countAiScope` — eligibility after the server
 * clears prior AI/bulk verdicts for a full re-run.
 */

const PRIMARY_BTN = {
  bg: '#fafafa',
  color: '#0a0a0a',
  borderRadius: 'full',
  fontWeight: '600',
  fontSize: '13px',
  h: '36px',
  _hover: { opacity: 0.85 },
  _active: { opacity: 0.7 },
};

const OUTLINE_BTN = {
  bg: 'transparent',
  borderRadius: 'full',
  fontWeight: '500',
  fontSize: '13px',
  h: '36px',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.6)',
  _hover: {
    borderColor: 'rgba(255,255,255,0.3)',
    color: 'rgba(255,255,255,0.9)',
    bg: 'rgba(255,255,255,0.04)',
  },
};

const CustomCheckbox = ({ checked, indeterminate = false, onClick }) => (
  <Box
    as="span"
    w="16px"
    h="16px"
    flexShrink={0}
    borderRadius="4px"
    border="1.5px solid"
    borderColor={checked || indeterminate ? '#fafafa' : 'rgba(255,255,255,0.3)'}
    bg={checked || indeterminate ? '#fafafa' : 'transparent'}
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    transition="all 0.12s"
    cursor="pointer"
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
  >
    {indeterminate && !checked ? (
      <Minus size={10} strokeWidth={2.5} color="#0a0a0a" />
    ) : checked ? (
      <Check size={10} strokeWidth={2.5} color="#0a0a0a" />
    ) : null}
  </Box>
);

const AiComplianceReviewModal = ({
  isOpen,
  onClose,
  drafts = [],
  contacts = [],
  onStart,
  selectedConfigIds = [],
  onSelectedConfigIdsChange,
}) => {
  const draftIds = useMemo(() => drafts.map((d) => d.config_id), [drafts]);

  const selectedDraftIds = useMemo(
    () => new Set(selectedConfigIds ?? []),
    [selectedConfigIds],
  );

  const allDraftsChecked =
    draftIds.length > 0 && selectedDraftIds.size === draftIds.length;
  const someDraftsChecked =
    selectedDraftIds.size > 0 && selectedDraftIds.size < draftIds.length;

  const toggleDraft = useCallback(
    (configId) => {
      const next = new Set(selectedConfigIds ?? []);
      if (next.has(configId)) next.delete(configId);
      else next.add(configId);
      onSelectedConfigIdsChange?.([...next]);
    },
    [selectedConfigIds, onSelectedConfigIdsChange],
  );

  const toggleAllDrafts = useCallback(() => {
    const allSelected =
      draftIds.length > 0 && selectedDraftIds.size === draftIds.length;
    onSelectedConfigIdsChange?.(allSelected ? [] : [...draftIds]);
  }, [draftIds, selectedDraftIds.size, onSelectedConfigIdsChange]);

  const { willReview, willSkip } = useMemo(
    () => countAiScope(contacts, selectedDraftIds),
    [contacts, selectedDraftIds],
  );

  const canRun = selectedDraftIds.size > 0 && willReview > 0;

  const handleStart = useCallback(() => {
    if (!canRun) return;
    // Close immediately — fire-and-forget. SSE updates land in the
    // cache via the parent's hook; the toolbar pill carries progress.
    onClose();
    onStart?.({ configIds: [...selectedDraftIds] });
  }, [canRun, onClose, onStart, selectedDraftIds]);

  const ctaLabel =
    willReview > 0
      ? `Run AI review on ${willReview.toLocaleString()} ${
          willReview === 1 ? 'cell' : 'cells'
        }`
      : 'Run AI review';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="rgba(0,0,0,0.85)" />
      <ModalContent
        bg="#111111"
        borderColor="rgba(255,255,255,0.10)"
        borderWidth="1px"
        borderRadius="10px"
        maxW="480px"
      >
        <ModalHeader
          pt={5}
          px={6}
          pb={4}
          borderBottom="1px solid"
          borderColor="rgba(255,255,255,0.08)"
        >
          <HStack justify="space-between" align="center" spacing={3}>
            <HStack spacing={2.5} minW={0}>
              <Box
                p={1.5}
                borderRadius="6px"
                bg="rgba(255,255,255,0.06)"
                border="1px solid rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                flexShrink={0}
              >
                <Sparkles size={13} strokeWidth={1.75} color="rgba(255,255,255,0.7)" />
              </Box>
              <Text
                fontSize="11px"
                fontWeight="600"
                color="rgba(255,255,255,0.45)"
                letterSpacing="0.06em"
                textTransform="uppercase"
                lineHeight="1.35"
              >
                AI review — message drafts
              </Text>
            </HStack>
            <HStack spacing={2} flexShrink={0} align="center">
              <Text fontSize="xs" color="rgba(255,255,255,0.35)" whiteSpace="nowrap">
                {selectedDraftIds.size} / {drafts.length} selected
              </Text>
              <IconButton
                aria-label="Close"
                icon={<X size={16} strokeWidth={2} />}
                size="sm"
                variant="ghost"
                color="rgba(255,255,255,0.4)"
                borderRadius="full"
                minW="32px"
                h="32px"
                onClick={onClose}
                _hover={{
                  color: 'rgba(255,255,255,0.75)',
                  bg: 'rgba(255,255,255,0.06)',
                }}
              />
            </HStack>
          </HStack>
        </ModalHeader>

        <ModalBody py={5}>
          <VStack spacing={5} align="stretch">
            {drafts.length === 0 ? (
              <Box py={4} textAlign="center">
                <Text fontSize="sm" color="rgba(255,255,255,0.3)">
                  No drafts on this campaign yet.
                </Text>
              </Box>
            ) : (
              <VStack spacing={0} align="stretch">
                <HStack
                  spacing={3}
                  px={3}
                  py={2}
                  borderRadius="7px"
                  _hover={{ bg: 'rgba(255,255,255,0.03)' }}
                  cursor="pointer"
                  onClick={toggleAllDrafts}
                  userSelect="none"
                  mb={1}
                >
                  <CustomCheckbox
                    checked={allDraftsChecked}
                    indeterminate={someDraftsChecked}
                    onClick={toggleAllDrafts}
                  />
                  <Text fontSize="xs" fontWeight="500" color="rgba(255,255,255,0.6)">
                    {allDraftsChecked ? 'Deselect all' : 'Select all'}
                  </Text>
                </HStack>

                <Divider borderColor="rgba(255,255,255,0.06)" mb={1} />

                <Box
                  maxH="180px"
                  overflowY="auto"
                  sx={{
                    '&::-webkit-scrollbar': { w: '4px' },
                    '&::-webkit-scrollbar-thumb': {
                      bg: 'rgba(255,255,255,0.1)',
                      borderRadius: 'full',
                    },
                  }}
                >
                  <VStack spacing={1} align="stretch">
                    {drafts.map((draft) => {
                      const checked = selectedDraftIds.has(draft.config_id);
                      return (
                        <HStack
                          key={draft.config_id}
                          spacing={3}
                          px={3}
                          py={2.5}
                          borderRadius="7px"
                          bg={checked ? 'rgba(255,255,255,0.07)' : 'transparent'}
                          border="1px solid"
                          borderColor={
                            checked
                              ? 'rgba(255,255,255,0.18)'
                              : 'rgba(255,255,255,0.04)'
                          }
                          cursor="pointer"
                          transition="all 0.12s"
                          _hover={{
                            bg: checked
                              ? 'rgba(255,255,255,0.09)'
                              : 'rgba(255,255,255,0.04)',
                            borderColor: 'rgba(255,255,255,0.18)',
                          }}
                          onClick={() => toggleDraft(draft.config_id)}
                          userSelect="none"
                        >
                          <CustomCheckbox
                            checked={checked}
                            onClick={() => toggleDraft(draft.config_id)}
                          />
                          <Text
                            fontSize="xs"
                            fontWeight="500"
                            color="#fafafa"
                            noOfLines={1}
                            flex={1}
                            minW={0}
                          >
                            {draft.display_name || 'Untitled draft'}
                          </Text>
                        </HStack>
                      );
                    })}
                  </VStack>
                </Box>
              </VStack>
            )}

            {/* Preview strip */}
            <Box
              px={3}
              py={2.5}
              borderRadius="8px"
              bg="rgba(255,255,255,0.03)"
              border="1px solid rgba(255,255,255,0.06)"
            >
              <HStack spacing={4} justify="space-between" fontSize="xs">
                <HStack spacing={2}>
                  <Sparkles size={11} strokeWidth={2} color="#A78BFA" />
                  <Text color="rgba(255,255,255,0.75)" fontWeight="500">
                    Will review {willReview.toLocaleString()}{' '}
                    {willReview === 1 ? 'cell' : 'cells'}
                  </Text>
                </HStack>
                {willSkip > 0 && (
                  <Text color="rgba(255,255,255,0.45)">
                    Skip {willSkip.toLocaleString()} already reviewed or up-to-date
                  </Text>
                )}
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="rgba(255,255,255,0.08)" pt={4}>
          <HStack spacing={2}>
            <Button {...OUTLINE_BTN} onClick={onClose}>
              Cancel
            </Button>
            <Button {...PRIMARY_BTN} onClick={handleStart} isDisabled={!canRun}>
              {ctaLabel}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AiComplianceReviewModal;
